import { useState } from "react";

import { Server } from "@wajeez/wajeez.microservices";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import ContentCarousel from "@components/items/display/ContentCarousel";
import BookCard from "@components/items/display/BookCard";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";

import { HorizontalListSkeleton } from "./HorizontalListSkeleton";
import { useLanguage } from "@modules/localizations/LanguageContext";
import MarkdownView from "react-showdown";
import TranslatedText from "@components/items/display/TranslatedText";
import { ChevronLeft } from "lucide-react";
import { OnItemPress } from "@modules/content/hooks/useComponentData";

export interface OtherAyatListParameters {
  title: string;
  //   seeAllTitle: string;
  //   withProgress: "False" | "True";
  //   newBadgeText: string;
  //   newBadgeShown: "False" | "True";
  //   newBadgeTextColor: string;
  //   newBadgeBackgroundColor: string;
  //   withCompleted: "False" | "True";
}

export interface OtherAyatListItemParameters {
  text: string | null;
  source: string | null;
  date: string | null;
}

type ItemParams = {
  id: string | undefined;
} & {
  [K in keyof OtherAyatListItemParameters]?:
    | OtherAyatListItemParameters[K]
    | undefined;
};

interface OtherAyatListProps {
  onItemPress: OnItemPress<ItemParams>;
  contents:
    | (Server.ContentEntityOutput & {
        isLocked: boolean | undefined;
        lockType: Server.LockType | undefined;
      })[]
    | undefined;
  isLoading: boolean;
  parameterValues: OtherAyatListParameters;
  items: ItemParams[];
}

export const OtherAyatList = ({
  onItemPress,
  contents,
  isLoading,
  parameterValues,
  items,
}: OtherAyatListProps) => {
  const navigate = useAppNavigate();

  const [seeAllShown, setSeeAllShown] = useState(false);

  const { direction } = useLanguage();

  const { title } = parameterValues;

  const isLoadingVisible = useIsLoadingVisibile(
    isLoading && (!contents || Boolean(contents && !contents.length))
  );

  return (
    <div
      className={`relative ${(isLoadingVisible || isLoading) && "h-[350px]"}`}
    >
      <div
        className={`absolute top-0 h-full w-full px-10 ltr:left-0 rtl:right-0 ${
          (isLoadingVisible || isLoading) && "z-10 bg-white dark:bg-black"
        }`}
      >
        <OtherAyatListLoading isLoading={isLoadingVisible} />
      </div>
      {contents && contents.length ? (
        <div className="flex w-full flex-col items-center justify-center gap-y-6 px-10 pb-[35px]">
          <div className="flex w-full items-center justify-between">
            <div
              className={`text-[20px] font-bold text-ramadan-primary dark:text-dark-ramadan-primary`}
            >
              {title}
            </div>
          </div>
          <div className="flex w-full items-center justify-start">
            <ContentCarousel
              className="w-[95%] sm:max-w-[700px] md:max-w-[1150px] xl:max-w-[1500px]"
              itemClassName="sm:basis-1/3 md:basis-1/3 xl:basis-1/4"
              options={{
                direction,
              }}
              items={items.map((item, i) => {
                return (
                  <div
                    key={item.id}
                    className="relative h-[229px] w-[361px] cursor-pointer overflow-hidden rounded-[12px] bg-cover pb-[34px] pe-[21px] ps-[12px] pt-[38px] text-[17px] font-normal text-dark-ramadan-primary"
                    style={{
                      backgroundImage: "url(/assets/images/ayah-bg-2.png)",
                    }}
                    onClick={() => {
                      const content = contents.find((c) => c.id === item.id);

                      content &&
                        onItemPress(content, item, {
                          isLocked: content.isLocked,
                        });
                    }}
                  >
                    <div
                      className="absolute left-0 top-0 h-full w-full opacity-80"
                      style={{
                        background:
                          "linear-gradient(90deg, #163125 27.97%, #163125 63.5%, rgba(22, 49, 37, 0.53) 100%)",
                      }}
                    ></div>
                    <div className="relative z-10 h-full">
                      <div className="absolute -top-6 text-[15px] text-[#FFFFFF] opacity-50 ltr:left-0 rtl:right-0">
                        {item.date}
                      </div>
                      <MarkdownView
                        markdown={item.text || ""}
                        options={{ tables: true, emoji: true }}
                        className="flex h-full flex-col items-start justify-center gap-y-2 overflow-hidden leading-[30px] *:hidden first:*:block first:*:h-[85px] first:*:overflow-hidden"
                      />
                      <div className="absolute -bottom-5 flex items-center justify-center text-[15px] text-[#FFFFFF] ltr:-right-2 rtl:-left-2">
                        <TranslatedText text="content-details.ramadan.ayah explanation" />
                        <ChevronLeft />
                      </div>
                    </div>
                  </div>
                );
              })}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const OtherAyatListLoading = ({ isLoading }: { isLoading: boolean }) => {
  return isLoading ? <HorizontalListSkeleton /> : null;
};
